import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { BASE_API } from "@/constants";

/**
 * Custom hook for fetching data with support for params and reloading.
 *
 * @param {string} url - The API endpoint URL
 * @param {object} params - Query parameters object (will be stringified)
 * @param {boolean} immediate - Whether to fetch immediately on mount (default true)
 */
const useFetchData = (url, params = {}, immediate = true) => {
    const [data, setData] = useState(null);
    const [isLoading, setIsLoading] = useState(immediate);
    const [error, setError] = useState(null);

    // Memoize the params to avoid infinite loops if the object reference changes
    // User should pass a memoized object or we rely on JSON.stringify for eff dependency
    const paramsString = JSON.stringify(params);

    const fetchData = useCallback(async () => {
        if (!url) return;
        setIsLoading(true);
        setError(null);
        try {
            const response = await axios.get(`${BASE_API}/${url}`, { params });
            setData(response.data.data);
        } catch (err) {
            setError(err);
            console.error("Error fetching data:", err);
        } finally {
            setIsLoading(false);
        }
    }, [url, paramsString]);

    useEffect(() => {
        if (immediate && url) {
            fetchData();
        } else if (!url) {
            setIsLoading(false);
        }
    }, [fetchData, immediate, url]);

    return { data, isLoading, error, refetch: fetchData };
};

export default useFetchData;
